
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useSearch } from "@/context/SearchContext";
import { usePropertyTypes } from "@/hooks/usePropertyTypes";
import { Building, DollarSign, Filter, MapPin, X } from "lucide-react";
import { useState } from "react";
import FilterChipSection from "./FilterChipSection";
import PriceRangeSection from "./PriceRangeSection";

// Popular cities for location chips
const popularLocations = [
  "Colombo 03",
  "Colombo 04",
  "Colombo 05",
  "Colombo 07",
  "Nugegoda",
  "Wellawatta",
];

// Price range options
const priceRangeOptions = [
  { label: "Under $50K", value: "0-50000" },
  { label: "$50K-$100K", value: "50000-100000" },
  { label: "$100K-$500K", value: "100000-500000" },
  { label: "$500K-$1M", value: "500000-1000000" },
  { label: "Over $1M", value: "1000000-" },
];

const MinimizedFilterSidebar = () => {
  const { activeFilters, handleFloatingFilterChange } = useSearch();
  const { propertyTypes = [], isLoading } = usePropertyTypes();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState<
    "locations" | "propertyTypes" | "priceRange" | null
  >(null);

  // Get property type names from the API response, ensure no empty values
  const propertyTypeOptions = propertyTypes
    ?.filter(type => type && type.name)
    ?.map(type => type.name) || [];

  const toggleSection = (
    section: "locations" | "propertyTypes" | "priceRange"
  ) => {
    if (activeSection === section) {
      setActiveSection(null);
    } else {
      setActiveSection(section);
      setIsExpanded(true);
    }
  };

  const closeFilters = () => {
    setIsExpanded(false);
    setActiveSection(null);
  };

  // Safely access activeFilters properties with null checks
  const locationsCount = activeFilters?.locations?.length || 0;
  const propertyTypesCount = activeFilters?.propertyTypes?.length || 0;
  const hasPriceRange = activeFilters?.priceRange ? 1 : 0;

  const totalActiveFilters =
    locationsCount + propertyTypesCount + hasPriceRange;

  return (
    <div className="fixed left-0 bottom-10 z-40">
      {/* Collapsed Filter Button */}
      {!isExpanded && (
        <Button
          onClick={() => setIsExpanded(true)}
          className="rounded-r-lg rounded-l-none h-auto py-3 px-2 shadow-md"
        >
          <Filter className="w-5 h-5" />
          {totalActiveFilters > 0 && (
            <Badge className="absolute -top-2 -right-2 bg-amber-500 text-white">
              {totalActiveFilters}
            </Badge>
          )}
        </Button>
      )}

      {/* Expanded Filter Panel */}
      {isExpanded && (
        <Card className="rounded-r-lg rounded-l-none shadow-lg border border-gray-200 w-64 bg-white animate-slide-right">
          <div className="flex items-center justify-between p-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              <span className="font-medium text-sm">Quick Filters</span>

              {totalActiveFilters > 0 && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700">
                  {totalActiveFilters}
                </Badge>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={closeFilters}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="p-3 space-y-2">
            <Button
              variant="outline"
              className={`w-full justify-start ${
                locationsCount > 0 ? "bg-amber-50 border-amber-200" : ""
              }`}
              onClick={() => toggleSection("locations")}
            >
              <MapPin className="w-4 h-4 mr-2" />
              Locations
              {locationsCount > 0 && (
                <Badge className="ml-auto bg-amber-500">{locationsCount}</Badge>
              )}
            </Button>

            <Button
              variant="outline"
              className={`w-full justify-start ${
                propertyTypesCount > 0 ? "bg-blue-50 border-blue-200" : ""
              }`}
              onClick={() => toggleSection("propertyTypes")}
            >
              <Building className="w-4 h-4 mr-2" />
              Property Types
              {propertyTypesCount > 0 && (
                <Badge className="ml-auto bg-blue-500">
                  {propertyTypesCount}
                </Badge>
              )}
            </Button>

            <Button
              variant="outline"
              className={`w-full justify-start ${
                activeFilters?.priceRange ? "bg-green-50 border-green-200" : ""
              }`}
              onClick={() => toggleSection("priceRange")}
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Price Range
              {activeFilters?.priceRange && (
                <Badge className="ml-auto bg-green-500">1</Badge>
              )}
            </Button>
          </div>

          {/* Expanded sections */}
          {activeSection === "locations" && (
            <FilterChipSection
              title="POPULAR LOCATIONS"
              options={popularLocations}
              selectedOptions={activeFilters?.locations || []}
              icon={<MapPin className="w-3 h-3" />}
              onOptionClick={(location) =>
                handleFloatingFilterChange("location", location)
              }
              variant="outline"
              size="sm"
            />
          )}

          {activeSection === "propertyTypes" && (
            <FilterChipSection
              title="PROPERTY TYPES"
              options={propertyTypeOptions}
              selectedOptions={activeFilters?.propertyTypes || []}
              icon={<Building className="w-3 h-3" />}
              onOptionClick={(type) =>
                handleFloatingFilterChange("propertyType", type)
              }
              variant="outline"
              size="sm"
              isLoading={isLoading}
            />
          )}

          {activeSection === "priceRange" && (
            <PriceRangeSection
              priceRangeOptions={priceRangeOptions}
              selectedPriceRange={activeFilters?.priceRange || null}
              onPriceRangeChange={(value) =>
                handleFloatingFilterChange("priceRange", value)
              }
            />
          )}
        </Card>
      )}
    </div>
  );
};

export default MinimizedFilterSidebar;
